import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, Chip, Avatar, IconButton, Divider, LinearProgress } from '@mui/material';
import { MoreHoriz, TrendingUp, TrendingDown, People, CalendarMonth, Inventory2, AttachMoney, NavigateBefore, NavigateNext, Add } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, getDay } from 'date-fns';

// Mini Calendar
const MiniCalendar = ({ appointments }: { appointments: any[] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const hasAppointment = (day: Date) =>
    appointments.some(a => isSameDay(new Date(a.appointmentDate), day));

  const selectedAppts = appointments.filter(a => isSameDay(new Date(a.appointmentDate), selectedDay));

  return (
    <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Month Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} sx={{ width: 26, height: 26, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <NavigateBefore sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} sx={{ width: 26, height: 26, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <NavigateNext sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Day names */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3, mb: 0.5 }}>
          {dayNames.map(d => (
            <Typography key={d} sx={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8', py: 0.3 }}>{d}</Typography>
          ))}
        </Box>

        {/* Days grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3 }}>
          {Array.from({ length: startPad }).map((_, i) => <Box key={`pad-${i}`} />)}
          {days.map(day => {
            const today = isToday(day);
            const selected = isSameDay(day, selectedDay);
            const hasAppt = hasAppointment(day);
            return (
              <Box key={day.toString()} onClick={() => setSelectedDay(day)}
                sx={{
                  textAlign: 'center', cursor: 'pointer', py: 0.6, borderRadius: 1.5, position: 'relative',
                  bgcolor: selected ? '#0d9488' : today ? '#f0fdfa' : 'transparent',
                  border: today && !selected ? '1px solid #0d9488' : '1px solid transparent',
                  '&:hover': { bgcolor: selected ? '#0f766e' : '#f0fdfa' },
                  transition: 'all 0.15s'
                }}>
                <Typography sx={{ fontSize: 12, fontWeight: selected || today ? 700 : 400, color: selected ? 'white' : today ? '#0d9488' : '#374151' }}>
                  {format(day, 'd')}
                </Typography>
                {hasAppt && !selected && (
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#0d9488', mx: 'auto', mt: 0.2 }} />
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Selected Day Schedule */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
            {format(selectedDay, 'EEEE, d MMMM')}
          </Typography>
          <IconButton size="small" sx={{ width: 22, height: 22, bgcolor: '#0d9488', borderRadius: 1 }}>
            <Add sx={{ fontSize: 14, color: 'white' }} />
          </IconButton>
        </Box>

        {selectedAppts.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', py: 2 }}>No appointments</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 160, overflowY: 'auto' }}>
            {selectedAppts.slice(0, 4).map(appt => (
              <Box key={appt._id} sx={{ p: 1.2, bgcolor: '#f0fdfa', borderRadius: 2, borderLeft: '3px solid #0d9488' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{appt.patient?.name}</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{appt.timeSlot} — {appt.type}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Stat Card
const StatCard = ({ title, value, subtitle, icon, color, bg, trend, trendUp }: any) => (
  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, '&:hover': { boxShadow: '0 4px 20px rgba(13,148,136,0.12)', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
        </Box>
        <IconButton size="small" sx={{ width: 28, height: 28, color: '#94a3b8' }}><MoreHoriz fontSize="small" /></IconButton>
      </Box>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{value ?? '—'}</Typography>
      <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.4, fontWeight: 500 }}>{title}</Typography>
      {trend !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          {trendUp ? <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} /> : <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />}
          <Typography sx={{ fontSize: 12, color: trendUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
            {trendUp ? '+' : ''}{trend}%
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{subtitle}</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <Box sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>{label}</Typography>
        {payload.map((p: any) => (
          <Typography key={p.name} sx={{ fontSize: 12, color: p.color }}>{p.name}: <strong>{p.value}</strong></Typography>
        ))}
      </Box>
    );
  }
  return null;
};

const PATIENT_AGE_COLORS = ['#0d9488', '#14b8a6', '#5eead4', '#99f6e4'];

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [todayAppts, setTodayAppts] = useState<any[]>([]);
  const [allAppts, setAllAppts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock chart data
  const patientBarData = [
    { date: '1 Jun', Child: 12, Adult: 28, Elderly: 8 },
    { date: '2 Jun', Child: 15, Adult: 32, Elderly: 11 },
    { date: '3 Jun', Child: 9,  Adult: 25, Elderly: 7 },
    { date: '4 Jun', Child: 18, Adult: 35, Elderly: 12 },
    { date: '5 Jun', Child: 14, Adult: 30, Elderly: 9 },
    { date: '6 Jun', Child: 20, Adult: 38, Elderly: 14 },
    { date: '7 Jun', Child: 16, Adult: 33, Elderly: 10 },
  ];

  const revenueData = [
    { day: 'Sun', Income: 800, Expense: 400 },
    { day: 'Mon', Income: 1200, Expense: 600 },
    { day: 'Tue', Income: 900, Expense: 500 },
    { day: 'Wed', Income: 1495, Expense: 700 },
    { day: 'Thu', Income: 1100, Expense: 550 },
    { day: 'Fri', Income: 1300, Expense: 650 },
    { day: 'Sat', Income: 950, Expense: 480 },
  ];

  const pieData = [
    { name: 'General', value: 35 },
    { name: 'Cardiology', value: 25 },
    { name: 'Pediatrics', value: 20 },
    { name: 'Others', value: 20 },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const hospitalId = user?.hospital?._id;
        const [apptRes, statsRes, allApptRes] = await Promise.all([
          api.get('/appointments/today'),
          hospitalId ? api.get(`/hospitals/${hospitalId}/stats`) : Promise.resolve({ data: { data: {} } }),
          api.get('/appointments', { params: { limit: 100 } }).catch(() => ({ data: { data: [] } }))
        ]);
        setTodayAppts(apptRes.data.data || []);
        setStats(statsRes.data.data || {});
        setAllAppts(allApptRes.data.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>;

  const statusColors: Record<string, any> = {
    scheduled: { color: '#3b82f6', bg: '#eff6ff' }, confirmed: { color: '#10b981', bg: '#ecfdf5' },
    'checked-in': { color: '#8b5cf6', bg: '#f5f3ff' }, 'in-progress': { color: '#f59e0b', bg: '#fffbeb' },
    completed: { color: '#059669', bg: '#ecfdf5' }, cancelled: { color: '#ef4444', bg: '#fef2f2' },
  };

  const doctors = [
    { name: 'Dr. Petra Winsbury', dept: 'General Medicine', status: 'available', time: '09:00 AM - 12:00 PM' },
    { name: 'Dr. Ameena Karim', dept: 'Orthopedics', status: 'unavailable', time: '02:00 PM - 05:00 PM' },
    { name: 'Dr. Rajesh Kumar', dept: 'Cardiology', status: 'available', time: '10:00 AM - 01:00 PM' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]} 👋
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14, mt: 0.3 }}>
          {format(new Date(), 'EEEE, d MMMM yyyy')} · Hospital Management Dashboard
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard title="Total Patients" value={stats?.totalPatients?.toLocaleString() || '1,890'} icon={<People />} color="#0d9488" bg="#f0fdfa" trend={3.78} trendUp subtitle="vs yesterday" />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard title="Total Invoices" value={stats?.totalRevenue ? `₹${(stats.totalRevenue/1000).toFixed(0)}K` : '₹1,287'} icon={<AttachMoney />} color="#6366f1" bg="#eef2ff" trend={2.14} trendUp subtitle="vs yesterday" />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard title="Appointments" value={stats?.todayAppointments || todayAppts.length || 128} icon={<CalendarMonth />} color="#f59e0b" bg="#fffbeb" trend={1.56} trendUp={false} subtitle="vs yesterday" />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard title="Available Beds" value={stats?.availableBeds || 315} icon={<Inventory2 />} color="#ec4899" bg="#fdf2f8" trend={1.64} trendUp subtitle="vs yesterday" />
        </Grid>
      </Grid>

      {/* Main Grid */}
      <Grid container spacing={2.5}>

        {/* Patient Overview Bar Chart */}
        <Grid item xs={12} lg={5}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: 320 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>Patient Overview</Typography>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>by Age Groups</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {[{ label: 'Child', color: '#5eead4' }, { label: 'Adult', color: '#0d9488' }, { label: 'Elderly', color: '#0f766e' }].map(l => (
                    <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color }} />
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>{l.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={patientBarData} barGap={2} barSize={8}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Child" fill="#5eead4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Adult" fill="#0d9488" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Elderly" fill="#0f766e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Line Chart */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: 320 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>Revenue</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {['Week', 'Month', 'Year'].map(p => (
                    <Chip key={p} label={p} size="small" sx={{ fontSize: 11, height: 24, bgcolor: p === 'Week' ? '#0d9488' : '#f8fafc', color: p === 'Week' ? 'white' : '#64748b', cursor: 'pointer' }} />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                {[{ label: 'Income', color: '#0d9488' }, { label: 'Expense', color: '#5eead4' }].map(l => (
                  <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 3, borderRadius: 2, bgcolor: l.color }} />
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{l.label}</Typography>
                  </Box>
                ))}
              </Box>
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="income" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0d9488" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expense" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5eead4" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#5eead4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="Income" stroke="#0d9488" strokeWidth={2.5} fill="url(#income)" dot={false} />
                  <Area type="monotone" dataKey="Expense" stroke="#5eead4" strokeWidth={2.5} fill="url(#expense)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Mini Calendar */}
        <Grid item xs={12} lg={3}>
          <MiniCalendar appointments={allAppts} />
        </Grid>

        {/* Patient Overview by Department (Pie) */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>Patient Overview</Typography>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>by Departments</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PieChart width={180} height={180}>
                    <Pie data={pieData} cx={85} cy={85} innerRadius={52} outerRadius={82} paddingAngle={3} dataKey="value" stroke="none">
                      {pieData.map((_, i) => <Cell key={i} fill={PATIENT_AGE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                  <Box sx={{ position: 'absolute', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>1,890</Typography>
                    <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Overall</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                {pieData.map((p, i) => (
                  <Box key={p.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PATIENT_AGE_COLORS[i] }} />
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{p.name}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.value}%</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Doctors Schedule */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>Doctors' Schedule</Typography>
                <IconButton size="small"><MoreHoriz fontSize="small" /></IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {doctors.map((doc, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 38, height: 38, bgcolor: i % 2 === 0 ? '#f0fdfa' : '#eef2ff', color: i % 2 === 0 ? '#0d9488' : '#6366f1', fontWeight: 800, fontSize: 14 }}>
                      {doc.name.split(' ')[1]?.charAt(0)}{doc.name.split(' ')[2]?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }} noWrap>{doc.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{doc.dept}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>{doc.time}</Typography>
                    </Box>
                    <Chip label={doc.status} size="small" sx={{
                      fontSize: 10, height: 20, textTransform: 'capitalize',
                      bgcolor: doc.status === 'available' ? '#dcfce7' : '#fee2e2',
                      color: doc.status === 'available' ? '#15803d' : '#b91c1c',
                    }} />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Appointments */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>Today's Queue</Typography>
                <Chip label={`${todayAppts.length} total`} size="small" sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontSize: 11 }} />
              </Box>
              {todayAppts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarMonth sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>No appointments today</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 260, overflowY: 'auto' }}>
                  {todayAppts.map(a => {
                    const sc = statusColors[a.status] || statusColors.scheduled;
                    return (
                      <Box key={a._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 11 }}>#{a.tokenNumber}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }} noWrap>{a.patient?.name}</Typography>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{a.timeSlot}</Typography>
                        </Box>
                        <Chip label={a.status} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontSize: 10, textTransform: 'capitalize', height: 20 }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
